import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';

import {DbCalendar} from './db-calendar';
import {DbDate} from './db-date';
import {DatepickerServiceInputs, DbDatepickerService} from './datepicker.service';
import {DatepickerViewModel, NavigationEvent} from './datepicker-view.model';
import {DbDatepickerConfig} from './datepicker-config';
import {NgbDateAdapter} from './adapters/ngb-date-adapter';
import {DbDateStruct} from './date-struct';
import {isChangedMonth} from './datepicker-tools';

/** An event emitted right before the navigation happens and the month displayed by the datepicker changes.*/
export interface IDatepickerNavigateEvent {
  /** The currently displayed month.*/
  current: {year: number, month: number} | null;

  /** The month we're navigating to.*/
  next: {year: number, month: number};

  /** Calling this function will prevent navigation from happening.*/
  preventDefault: () => void;
}

/** An interface that represents the readonly public state of the datepicker.
 *
 * Accessible via the `datepicker.state` getter */
export interface IDbDatepickerState {
  /** The earliest date that can be displayed or selected*/
  readonly minDate: DbDate | null;

  /** The latest date that can be displayed or selected*/
  readonly maxDate: DbDate | null;

  /** The first visible date of currently displayed months*/
  readonly firstDate: DbDate;

  /** The last visible date of currently displayed months*/
  readonly lastDate: DbDate;

  /** The date currently focused by the datepicker*/
  readonly focusedDate: DbDate;

  /** First dates of months currently displayed by the datepicker*/
  readonly months: DbDate[];
}

@Component({
    exportAs: 'dbDatepicker',
    selector: 'db-datepicker',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./datepicker.scss'],
    template: `
    <div>
      <db-datepicker-navigation
        [date]="model.firstDate!"
        [months]="model.months"
        [disabled]="model.disabled"
        [prevDisabled]="model.prevDisabled"
        [nextDisabled]="model.nextDisabled"
        [selectBoxes]="model.selectBoxes"
        (navigate)="onNavigateEvent($event)"
        (select)="onNavigateDateSelect($event)">
      </db-datepicker-navigation>
    </div>

    <div class="db-datepicker-content db-datepicker-months" #content>
      <div *ngFor="let month of model.months; let i = index;" class="db-datepicker-month">
        <db-datepicker-month [month]="month.firstDate" [startDate]="startDate" [endDate]="endDate"></db-datepicker-month>
      </div>
    </div>

    <div class="db-datepicker-footer">
      <a class="mr15" (click)="clearDate()">Clear</a>
      <button class="ds-btn-blue ds-btn-small" [disabled]="!startDate || (canSelectRange && !endDate)"
              (click)="applyDateSelect()"
      ><span>apply</span></button>
    </div>
  `,
    providers: [DbDatepickerService],
    standalone: false
})
export class DbDatepickerComponent implements OnDestroy, OnChanges, OnInit {

  model: DatepickerViewModel;

  @ViewChild('content') private _contentEl: ElementRef<HTMLElement>;

  private _destroyed$ = new Subject<void>();
  private _publicState: IDbDatepickerState = <any>{};

  /** The number of months to display. */
  @Input() displayMonths: number;

  /** The first day of the week.
   *
   * With default calendar we use ISO 8601: 'weekday' is 1=Mon ... 7=Sun.*/
  @Input() firstDayOfWeek: number;

  /** The reference to the custom template for the datepicker footer.*/
  @Input() footerTemplate: TemplateRef<any>;

  /**
   * The callback to mark some dates as disabled.
   *
   * It is called for each new date when navigating to a different month.
   *
   * `current` is the month that is currently displayed by the datepicker.
   */
  @Input() markDisabled: (date: DbDate, current?: {year: number, month: number}) => boolean;

  /**
   * The latest date that can be displayed or selected.
   *
   * If not provided, 'year' select box will display 10 years after the current month.
   */
  @Input() maxDate: DbDateStruct;

  /**
   * The earliest date that can be displayed or selected.
   *
   * If not provided, 'year' select box will display 10 years before the current month.
   */
  @Input() minDate: DbDateStruct;

  /**
   * The way of displaying days that don't belong to the current month.
   *
   * * `"visible"` - days are visible
   * * `"hidden"` - days are hidden, white space preserved
   * * `"collapsed"` - days are collapsed, so the datepicker height might change between months
   *
   * For the 2+ months view, days in between months are never shown.
   */
  @Input() outsideDays: 'visible' | 'collapsed' | 'hidden';

  /**
   * The date to open calendar with.
   *
   * With the default calendar we use ISO 8601: 'month' is 1=Jan ... 12=Dec.
   * If nothing or invalid date is provided, calendar will open with current month.
   *
   * You could use `navigateTo(date)` method as an alternative.
   */
  @Input() startDate: DbDateStruct;

  /** The last selected date to open calendar with.*/
  @Input() endDate: DbDateStruct | null;

  @Input() canSelectRange: boolean;

  /** Emit dateSelect() on every date selection.*/
  @Input() selectOnClick: boolean;

  /**
   * An event emitted right before the navigation happens and displayed month changes.
   *
   * See [`IDatepickerNavigateEvent`](#/components/datepicker/api#IDatepickerNavigateEvent) for the payload info.
   */
  @Output() navigate = new EventEmitter<IDatepickerNavigateEvent>();

  /**
   * An event emitted when user selects a date.
   *
   * The payload of the event is currently selected `DbDate`.
   */
  @Output() dateSelect = new EventEmitter<[DbDate, DbDate]>();

  /**
   * An event emitted when user press Clear button.
   *
   * The payload of the event is void.
   */
  @Output() dateClear = new EventEmitter<void>();

  constructor(
      private _service: DbDatepickerService, private _calendar: DbCalendar,
      config: DbDatepickerConfig, cd: ChangeDetectorRef, private _elementRef: ElementRef<HTMLElement>,
      private _ngbDateAdapter: NgbDateAdapter<any>, private _ngZone: NgZone) {
    ['displayMonths', 'firstDayOfWeek', 'footerTemplate', 'markDisabled', 'minDate',
     'maxDate', 'navigation', 'outsideDays', 'startDate',  'endDate', 'canSelectRange', 'selectOnClick']
        .forEach(input => this[input] = config[input]);

    _service.dateSelect$.pipe(takeUntil(this._destroyed$)).subscribe(date => {
      this._onDateSelection(date);
      this.selectOnClick && this.applyDateSelect();
    });

    _service.model$.pipe(takeUntil(this._destroyed$)).subscribe(model => {
      const newDate = model.firstDate !;
      const oldDate = this.model ? this.model.firstDate : null;

      // update public state
      this._publicState = {
        maxDate: model.maxDate,
        minDate: model.minDate,
        firstDate: model.firstDate !,
        lastDate: model.lastDate !,
        focusedDate: model.focusDate !,
        months: model.months.map(viewModel => viewModel.firstDate)
      };

      let navigationPrevented = false;
      // emitting navigation event if the first month changes
      if (!newDate.equals(oldDate)) {
        this.navigate.emit({
          current: oldDate ? {year: oldDate.year, month: oldDate.month} : null,
          next: {year: newDate.year, month: newDate.month},
          preventDefault: () => navigationPrevented = true
        });

        // can't prevent the very first navigation
        if (navigationPrevented && oldDate !== null) {
          this._service.open(oldDate);
          return;
        }
      }

      this.model = model;

      cd.markForCheck();
    });
  }

  /** Returns the readonly public state of the datepicker*/
  get state(): IDbDatepickerState { return this._publicState; }

  /** Returns the calendar service used in the specific datepicker instance.*/
  get calendar(): DbCalendar { return this._calendar; }

  /** Focuses on given date.*/
  focusDate(date?: DbDateStruct | null): void { this._service.focus(DbDate.from(date)); }

  /** Selects focused date.*/
  focusSelect(): void { this._service.focusSelect(); }

  focus() {
    this._ngZone.onStable.asObservable().pipe(take(1)).subscribe(() => {
      const elementToFocus =
          this._elementRef.nativeElement.querySelector<HTMLDivElement>('div.db-datepicker-day[tabindex="0"]');
      if (elementToFocus) {
        elementToFocus.focus();
      }
    });
  }

  /**
   * Navigates to the provided date.
   *
   * With the default calendar we use ISO 8601: 'month' is 1=Jan ... 12=Dec.
   * If nothing or invalid date provided calendar will open current month.
   *
   * Use the `[startDate]` input as an alternative.
   */
  navigateTo(date?: {year: number, month: number, day?: number}) {
    this._service.open(DbDate.from(date ? date.day ? date as DbDateStruct : {...date, day: 1} : null));
  }

  ngOnDestroy() { this._destroyed$.next(); }

  ngOnInit() {
    if (this.model === undefined) {
      const inputs: DatepickerServiceInputs = {};
      ['displayMonths', 'markDisabled', 'firstDayOfWeek', 'navigation', 'minDate', 'maxDate', 'outsideDays']
          .forEach(name => inputs[name] = this[name]);
      this._service.set(inputs);

      this.navigateTo(this.startDate);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const inputs: DatepickerServiceInputs = {};
    ['displayMonths', 'markDisabled', 'firstDayOfWeek', 'navigation', 'minDate', 'maxDate', 'outsideDays']
        .filter(name => name in changes)
        .forEach(name => inputs[name] = this[name]);
    this._service.set(inputs);

    if ('startDate' in changes) {
      const {currentValue, previousValue} = changes['startDate'];
      if (isChangedMonth(previousValue, currentValue)) {
        this.navigateTo(this.startDate);
      }
    }
  }

  onDateSelect(date: DbDate) {
    this._service.focus(date);
    this._service.select(date, {emitEvent: true});
  }

  onNavigateDateSelect(date: DbDate) { this._service.open(date); }

  onNavigateEvent(event: NavigationEvent) {
    switch (event) {
      case NavigationEvent.PREV:
        this._service.open(this._calendar.getPrev(this.model.firstDate !, 'm', 1));
        break;
      case NavigationEvent.NEXT:
        this._service.open(this._calendar.getNext(this.model.firstDate !, 'm', 1));
        break;
    }
  }

  clearDate(): void {
    this.startDate = null;
    this.endDate = null;
    this._service.select(null, {emitEvent: true});
    this.dateClear.emit();
  }

  public applyDateSelect(): void {
    this.dateSelect.emit([
      this.startDate && new DbDate(this.startDate.year, this.startDate.month, this.startDate.day) || null,
      this.endDate && new DbDate(this.endDate.year, this.endDate.month, this.endDate.day) || null
    ]);
  }

  private _onDateSelection(date: DbDate) {
    this.canSelectRange ? this._rangeDateSelect(date) : this._singleDateSelect(date);

  }

  private _singleDateSelect(date: DbDate): void {
    this.endDate && (this.endDate = null);
    this.startDate = date;
  }

  private _rangeDateSelect(date: DbDate): void {
    if (!this.startDate && !this.endDate) {
      this.startDate = date;
    } else if (this.startDate && !this.endDate && date && (date.after(this.startDate) || date.equals(this.startDate))) {
      this.endDate = date;
    } else {
      this.endDate = null;
      this.startDate = date;
    }
  }
}
